/**
 * Date range presets matching the team's discussion (Ron / Rich / Jesika):
 * larger time chunks reduce single-month noise. The "Jan – <last full month>"
 * preset is Ron's window, made dynamic: it always ends at the most recent
 * fully-completed month, so on June 5 it reads "Jan – May" (May is complete,
 * June is not yet).
 */

const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function startOfYear(year: number): Date {
  return new Date(Date.UTC(year, 0, 1));
}

/**
 * The most recent fully-completed month relative to `ref`, as {year, month}
 * (month is 0-based). The first of the current month minus one day lands on
 * the last day of the previous month. In January this rolls back to December
 * of the prior year (the latest complete Jan–through-month window).
 */
export function lastFullMonth(ref: Date): { year: number; month: number } {
  const firstOfThisMonth = new Date(Date.UTC(ref.getUTCFullYear(), ref.getUTCMonth(), 1));
  const lastDayPrevMonth = new Date(firstOfThisMonth.getTime() - 86_400_000);
  return { year: lastDayPrevMonth.getUTCFullYear(), month: lastDayPrevMonth.getUTCMonth() };
}

export interface Range {
  from: string;
  to: string;
}

export interface Preset {
  id: string;
  label: string;
  range: () => Range;
}

const today = () => new Date();

export const presets: Preset[] = [
  {
    id: "ytd",
    label: "Year to date",
    range: () => ({ from: iso(startOfYear(today().getUTCFullYear())), to: iso(today()) }),
  },
  {
    id: "jan-last-full",
    label: `Jan – ${MONTH_ABBR[lastFullMonth(today()).month]}`,
    range: () => {
      const { year, month } = lastFullMonth(today());
      // from = Jan 1 of that year; to = last day of that month (day 0 of next month)
      return { from: iso(new Date(Date.UTC(year, 0, 1))), to: iso(new Date(Date.UTC(year, month + 1, 0))) };
    },
  },
  {
    id: "q1",
    label: "Q1",
    range: () => {
      const y = today().getUTCFullYear();
      return { from: iso(new Date(Date.UTC(y, 0, 1))), to: iso(new Date(Date.UTC(y, 2, 31))) };
    },
  },
  {
    id: "q2",
    label: "Q2",
    range: () => {
      const y = today().getUTCFullYear();
      return { from: iso(new Date(Date.UTC(y, 3, 1))), to: iso(new Date(Date.UTC(y, 5, 30))) };
    },
  },
  {
    id: "last-30",
    label: "Last 30 days",
    range: () => {
      const t = today();
      const f = new Date(t);
      f.setDate(t.getDate() - 30);
      return { from: iso(f), to: iso(t) };
    },
  },
  {
    id: "last-12m",
    label: "Last 12 months",
    range: () => {
      const t = today();
      const f = new Date(Date.UTC(t.getUTCFullYear() - 1, t.getUTCMonth(), t.getUTCDate()));
      return { from: iso(f), to: iso(t) };
    },
  },
];

/** Same range, one year earlier — for like-for-like comparison. */
export function priorYearRange(r: Range): Range {
  const dec = (s: string) => {
    const [y, m, d] = s.split("-").map(Number);
    const next = new Date(Date.UTC(y - 1, m - 1, d));
    return iso(next);
  };
  return { from: dec(r.from), to: dec(r.to) };
}
